import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faSearch,
  faCheckCircle,
  faClock,
  faPlus,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { partnerApi } from '../../services/api/partnerApi'
import type { PartnerCustomer } from '../../types/partner'

type StatusFilter = 'all' | 'active' | 'pending'

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function CustomerList() {
  const navigate = useNavigate()
  const [customers, setCustomers] = useState<PartnerCustomer[]>([])
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await partnerApi.listCustomers(statusFilter)
      setCustomers(res.customers)
    } catch (err: any) {
      setError(err?.message || 'Failed to load customers')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return customers
    const q = searchQuery.toLowerCase()
    return customers.filter((c) =>
      [c.email, c.firstName, c.lastName].some((v) => (v || '').toLowerCase().includes(q)),
    )
  }, [customers, searchQuery])

  const handleCancelInvite = async (pendingAccountId: string) => {
    setBusyId(pendingAccountId)
    setActionMessage(null)
    try {
      await partnerApi.cancelInvite(pendingAccountId)
      setActionMessage('Invitation cancelled.')
      await load()
    } catch (e: any) {
      setActionMessage(e?.message || 'Failed to cancel invite')
    } finally {
      setBusyId(null)
    }
  }

  if (isLoading && customers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-600">Loading customers...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FontAwesomeIcon icon={faUsers} className="h-6 w-6 mr-2 text-blue-600" />
            Customers
          </h1>
          <p className="mt-1 text-sm text-gray-600">Active customers and pending invites.</p>
        </div>
        <Link
          to="/partner/customers/invite"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
        >
          <FontAwesomeIcon icon={faPlus} /> Invite Customer
        </Link>
      </div>

      {actionMessage && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">{actionMessage}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex gap-2">
            {(['all', 'active', 'pending'] as StatusFilter[]).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  statusFilter === s
                    ? s === 'pending'
                      ? 'bg-yellow-600 text-white'
                      : s === 'active'
                        ? 'bg-green-600 text-white'
                        : 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex-1 relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4"
            />
            <input
              type="text"
              placeholder="Search by email or name…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Logins
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Devices
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    {searchQuery ? 'No customers match your search.' : 'No customers yet.'}
                  </td>
                </tr>
              ) : (
                filtered.map((c) => {
                  const rowKey = c.kind === 'active' ? `u-${c.userId}` : `p-${c.pendingAccountId}`
                  const fullName = [c.firstName, c.lastName].filter(Boolean).join(' ')
                  const detailPath =
                    c.kind === 'active' && c.userId
                      ? `/partner/customers/${c.userId}`
                      : c.pendingAccountId
                        ? `/partner/customers/pending/${c.pendingAccountId}`
                        : null
                  return (
                    <tr
                      key={rowKey}
                      className={`hover:bg-gray-50 ${detailPath ? 'cursor-pointer' : ''}`}
                      onClick={() => {
                        if (detailPath) navigate(detailPath)
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-blue-600">
                          {fullName || c.email}
                        </div>
                        <div className="text-xs text-gray-500">{c.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            c.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={c.status === 'active' ? faCheckCircle : faClock}
                            className="h-3 w-3 mr-1"
                          />
                          {c.status === 'active' ? 'Active' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{c.loginCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {formatDate(c.lastLoginAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{c.deviceCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {formatDate(c.joinedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {c.kind === 'pending' && c.pendingAccountId && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCancelInvite(c.pendingAccountId!)
                            }}
                            disabled={busyId === c.pendingAccountId}
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                          >
                            <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                            {busyId === c.pendingAccountId ? 'Cancelling…' : 'Cancel invite'}
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
